import L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { db } from '../../data/db';
import { sgarCount, sgarStatus, STATION_TYPES } from '../../data/domain';
import { useClients, useRodentPlans } from '../../data/hooks';
import type { LatLng, RodentPlan, SnapshotFrame, Station, StationType } from '../../data/types';
import { dateKey, escapeHtml, nowIso, plural, uid } from '../../lib/format';
import { currentPosition, locateAddress, locationErrorMessage, searchAddress, TOWNSVILLE } from '../../lib/geo';
import { isOnline } from '../../sync/connectivity';
import { putEntity, queueFile } from '../../sync/outbox';
import { AddressLookup } from '../../ui/Lookups';
import { useUI } from '../../ui/UIProvider';
import { Icon } from '../../ui/Icon';
import { EmptyState, PageHeading, PanelHeader, View } from '../../ui/View';
import { locationMarker, markerIcon, useSatelliteMap } from '../map/leaflet';
import { SiteSnapshot, type SiteSnapshotHandle } from './SiteSnapshot';
import { exportSnapshotPng, frameAround } from './snapshot';

interface Site { address: string; location: LatLng }

export function RodentPlannerView({ active }: { active: boolean }) {
  const user = useUser();
  const ui = useUI();
  const clients = useClients();
  const plans = useRodentPlans();
  const containerRef = useRef<HTMLDivElement>(null);
  const map = useSatelliteMap(containerRef, TOWNSVILLE, 16, active);
  const snapshotRef = useRef<SiteSnapshotHandle>(null);
  const panelRef = useRef<HTMLElement>(null);
  const markerRef = useRef<L.Layer | null>(null);
  const [clientId, setClientId] = useState('');
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState('Search an address, adjust the map frame, then create a site snapshot.');
  const [site, setSite] = useState<Site | null>(null);
  const [frame, setFrame] = useState<SnapshotFrame | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [stationType, setStationType] = useState<StationType>('fgar');
  const [planId, setPlanId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const client = clients.find((item) => item.id === clientId) ?? null;
  useEffect(() => { if (!clientId && clients.length) setClientId(clients[0].id); }, [clients, clientId]);

  // Tapping the map selects the exact site location.
  const siteRef = useRef(site); siteRef.current = site;
  useEffect(() => {
    if (!map) return;
    const onClick = (event: L.LeafletMouseEvent) => {
      setSite({ address: siteRef.current?.address || 'Selected map location', location: [event.latlng.lat, event.latlng.lng] });
      setFeedback('Location selected. Adjust the map frame if needed, then create a site snapshot.');
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map]);

  function showSite(next: Site, message: string) {
    setSite(next);
    if (map) {
      if (markerRef.current) map.removeLayer(markerRef.current);
      markerRef.current = L.marker(next.location, { icon: markerIcon('search') }).bindPopup(escapeHtml(next.address)).addTo(map);
      map.setView(next.location, 19);
    }
    setFeedback(message);
  }

  async function findAddress(query = search) {
    const value = query.trim();
    if (!value) { setFeedback('Enter an address to load the site map.'); return; }
    if (!isOnline()) {
      // Offline: fall back to coordinates this device already knows for the address.
      const cached = await locateAddress(value);
      if (cached) showSite({ address: value, location: cached }, 'Offline: loaded the saved location for this address. Previously viewed imagery is available.');
      else setFeedback('Address search needs a connection. Use your location or tap the map instead.');
      return;
    }
    setFeedback('Searching OpenStreetMap...');
    try {
      const [result] = await searchAddress(value);
      if (!result) { setFeedback('No matching address found. Try a fuller address.'); return; }
      showSite({ address: result.displayName, location: result.location }, 'Site found. Pan or zoom to frame the property, then create a site snapshot.');
    } catch { setFeedback('Address search is unavailable right now. Check your connection and try again.'); }
  }

  function chooseClient(id: string) {
    setClientId(id);
    const chosen = clients.find((item) => item.id === id);
    setSearch(chosen?.address || '');
    setPlanId(null);
    if (chosen?.location) showSite({ address: chosen.address, location: chosen.location }, 'Client site loaded. Pan or zoom to frame the property, then create a site snapshot.');
    else if (chosen?.address) findAddress(chosen.address);
  }

  async function useMyLocation() {
    if (!map) return;
    setFeedback('Requesting your location…');
    try {
      const position = await currentPosition();
      const location: LatLng = [position.coords.latitude, position.coords.longitude];
      if (markerRef.current) map.removeLayer(markerRef.current);
      markerRef.current = locationMarker(location, 'Your field location').addTo(map);
      map.setView(location, 19);
      setSite({ address: 'Current field location', location });
      setFeedback('Current field location loaded. Adjust the map frame, then create a site snapshot.');
    } catch (error) { setFeedback(locationErrorMessage(error)); }
  }

  function captureSnapshot() {
    if (!site || !map) { setFeedback('Search for a property before creating a site snapshot.'); return; }
    setFrame(frameAround(map.getCenter()));
    setFeedback('High-resolution site snapshot created. Roads and labels are overlaid for station placement.');
    setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function loadPlan(plan: RodentPlan) {
    const matched = clients.find((item) => item.id === plan.clientId) || clients.find((item) => item.name === plan.clientName);
    if (matched) setClientId(matched.id);
    setSearch(plan.address);
    setPlanId(plan.id);
    setSite({ address: plan.address, location: plan.location });
    map?.setView(plan.location, 19);
    setStations(plan.stations);
    if (plan.snapshot) { setFrame(plan.snapshot); setFeedback(`Loaded the saved site snapshot for ${plan.clientName}.`); setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120); }
    else { setFrame(null); setFeedback('This older plan has no usable snapshot. Create a new site snapshot to update it.'); }
  }

  // "Open plan" from a client profile, the compliance register or a notification.
  const request = ui.rodentPlanRequest;
  useEffect(() => {
    if (!request || !map) return;
    db.rodentPlans.get(request.planId).then((plan) => { if (plan) loadPlan(plan); });
    ui.close('rodentPlanRequest');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, map]);

  const currentPlan = (planId && plans.find((plan) => plan.id === planId)) || plans.find((plan) => (plan.clientId === clientId || plan.clientName === client?.name) && plan.address === site?.address) || null;

  async function savePlan() {
    if (!site || !frame || !snapshotRef.current?.element) { setFeedback('Create a site snapshot before saving this plan.'); return; }
    if (!client) { setFeedback('Choose the client this plan belongs to.'); return; }
    setSaving(true);
    setFeedback('Saving station plan and PNG attachment...');
    const existing = currentPlan && (currentPlan.clientId === client.id || currentPlan.clientName === client.name) ? currentPlan : null;
    const id = existing?.id ?? uid('rodent-plan-');
    const snapshotFileName = `${client.name}-rodent-station-plan.png`;
    let snapshotFileId: string | null = null;
    try {
      const png = await exportSnapshotPng(snapshotRef.current.element, frame, stations);
      snapshotFileId = await queueFile(png, { namespace: 'rodent-plans', key: id, name: snapshotFileName, type: 'image/png' }, `Rodent plan image for ${client.name}`);
    } catch (error) { console.warn('Unable to export rodent plan PNG', error); }
    const plan: RodentPlan = {
      id, clientId: client.id, clientName: client.name, address: site.address, location: site.location, stations, snapshot: frame,
      snapshotFileId: snapshotFileId ?? existing?.snapshotFileId ?? null, snapshotDocumentId: snapshotFileId ? null : existing?.snapshotDocumentId ?? null,
      snapshotFileName, savedAt: nowIso(), lastSiteReviewDate: existing?.lastSiteReviewDate, lastSiteReviewedBy: existing?.lastSiteReviewedBy
    };
    await putEntity('rodent-plans', plan, `Rodent plan for ${client.name}`);
    setPlanId(id);
    setSaving(false);
    setFeedback(snapshotFileId ? `Saved the site snapshot, PNG attachment and ${plural(stations.length, 'bait station')} to ${client.name}'s profile.` : 'Saved the station plan, but the PNG was unavailable while tiles were loading (or offline without cached imagery). Save again once the image has loaded.');
  }

  async function markReviewed() {
    if (!currentPlan) return;
    await putEntity('rodent-plans', { ...currentPlan, lastSiteReviewDate: dateKey(), lastSiteReviewedBy: user.displayName }, `SGAR site review for ${currentPlan.clientName}`);
    setFeedback(`Site review recorded for ${currentPlan.clientName}. The next SGAR review is due within 35 days.`);
  }

  const move = (index: number, direction: number) => setStations((list) => { const target = index + direction; if (target < 0 || target >= list.length) return list; const next = [...list]; [next[index], next[target]] = [next[target], next[index]]; return next; });
  const review = currentPlan && sgarCount(currentPlan) ? sgarStatus(currentPlan) : null;

  return (
    <View id="rodent" active={active}>
      <PageHeading eyebrow="Site planning" title="Rodent station planner" subtitle="Find the property, frame the site on the map, then create a detailed plan image for station placement.">
        <button id="useRodentLocation" className="btn btn-secondary" type="button" onClick={useMyLocation}><Icon name="locate" size={16} />Use my location</button>
        <button id="captureSiteSnapshot" className="btn btn-primary" onClick={captureSnapshot}>Create site snapshot</button>
      </PageHeading>
      <div className="toolbar">
        <div className="search-field search-field-button toolbar-grow">
          <Icon name="search" />
          <AddressLookup id="rodentSearch" placeholder="Search property address" aria-label="Search property address" value={search} onChange={setSearch} onPick={(result) => showSite({ address: result.displayName, location: result.location }, 'Site found. Pan or zoom to frame the property, then create a site snapshot.')} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); findAddress(); } }} />
          <button id="rodentSearchButton" className="btn btn-secondary btn-sm" onClick={() => findAddress()}>Search</button>
        </div>
        <label className="field-inline">Client
          <select id="rodentClient" value={clientId} onChange={(event) => chooseClient(event.target.value)}>
            {clients.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
      </div>
      <div className="map-frame" id="rodentWorkspace">
        <div className="osm-map site-plan" id="sitePlan" ref={containerRef}></div>
        <p className="map-caption" id="rodentFeedback" aria-live="polite">{feedback}</p>
      </div>
      {frame && (
        <section className="panel snapshot-panel" id="siteSnapshotPanel" ref={panelRef}>
          <PanelHeader title="Detailed site snapshot" summary="Tap the image to place a station. Drag to pan, pinch or scroll to zoom, tap a station to remove it.">
            {review && <button id="markStationReview" className="btn btn-secondary btn-sm" type="button" onClick={markReviewed}>Mark site reviewed</button>}
          </PanelHeader>
          <div className="snapshot-toolbar">
            <label className="field-inline">Station type
              <select id="stationType" value={stationType} onChange={(event) => setStationType(event.target.value as StationType)}>
                {Object.entries(STATION_TYPES).map(([value, type]) => <option key={value} value={value}>{type.label}</option>)}
              </select>
            </label>
            <div className="button-group">
              <button id="undoStation" className="icon-button" aria-label="Undo last station" title="Undo last station" disabled={!stations.length} onClick={() => setStations((list) => list.slice(0, -1))}><Icon name="undo" /></button>
              <button id="snapshotZoomOut" className="icon-button" aria-label="Zoom snapshot out" title="Zoom out" onClick={() => snapshotRef.current?.zoomBy(-0.1)}><Icon name="minus" /></button>
              <button id="snapshotZoomIn" className="icon-button" aria-label="Zoom snapshot in" title="Zoom in" onClick={() => snapshotRef.current?.zoomBy(0.1)}><Icon name="plus" /></button>
              <button id="cropSnapshot" className="icon-button" aria-label="Keep current snapshot crop" title="Keep current crop" onClick={() => setFeedback('Current snapshot crop kept. The saved plan will reopen at this exact view.')}><Icon name="crop" /></button>
            </div>
          </div>
          <div className="snapshot-workspace">
            <SiteSnapshot ref={snapshotRef} frame={frame} stations={stations} onFrameChange={setFrame} onPlace={(point) => setStations((list) => [...list, { ...point, type: stationType }])} onRemove={(index) => setStations((list) => list.filter((_, at) => at !== index))} />
            <aside className="station-panel" aria-label="Bait stations">
              <header className="station-panel-header">
                <b>Bait stations</b><span className="badge badge-neutral" id="stationCount">{stations.length} placed</span>
                {review && <span id="stationReviewStatus" className={`badge ${review.state === 'current' ? 'badge-success' : review.state === 'due' ? 'badge-warning' : 'badge-danger'}`}>{review.label}</span>}
              </header>
              <div id="stationList" className="station-list">
                {stations.length ? stations.map((station, index) => (
                  <div className="station-row" key={index}>
                    <span className={`station-number type-${station.type}`} aria-hidden="true">{index + 1}</span>
                    <span className="row-text"><b>Station {index + 1}</b><small>{station.lat.toFixed(6)}, {station.lng.toFixed(6)}</small></span>
                    <select aria-label={`Station ${index + 1} type`} value={station.type} onChange={(event) => setStations((list) => list.map((item, at) => (at === index ? { ...item, type: event.target.value as StationType } : item)))}>
                      {Object.entries(STATION_TYPES).map(([value, type]) => <option key={value} value={value}>{type.label}</option>)}
                    </select>
                    <span className="button-group station-row-actions">
                      <button className="icon-button icon-button-sm" type="button" aria-label={`Move station ${index + 1} up`} title="Move up" disabled={index === 0} onClick={() => move(index, -1)}><Icon name="chevronUp" size={16} /></button>
                      <button className="icon-button icon-button-sm" type="button" aria-label={`Move station ${index + 1} down`} title="Move down" disabled={index === stations.length - 1} onClick={() => move(index, 1)}><Icon name="chevronDown" size={16} /></button>
                      <button className="icon-button icon-button-sm" type="button" aria-label={`Remove station ${index + 1}`} title="Remove" onClick={() => setStations((list) => list.filter((_, at) => at !== index))}><Icon name="close" size={16} /></button>
                    </span>
                  </div>
                )) : <EmptyState>No stations placed on this snapshot.</EmptyState>}
              </div>
              <footer className="station-panel-actions">
                <button id="saveStations" className="btn btn-primary" disabled={saving} onClick={savePlan}>{saving ? 'Saving…' : 'Save plan'}</button>
                <button id="clearStations" className="btn btn-secondary" disabled={!stations.length} onClick={() => setStations([])}>Clear plan</button>
              </footer>
            </aside>
          </div>
        </section>
      )}
    </View>
  );
}
